import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { GzInputSearchWebComponent } from '../../../ui/gzInput';
import { EasmRelatedFindingsViewData, MIN_NR_CHARS_SEARCH } from '../types';
import { translateFn } from '../../../utils/translator.helper';
import { FindingClassType, FindingType, findingTypeToIconMap } from '../../types';
import { FontSizes, FontWeights, TemplateState } from '../../../types/common/types';
import { GzAccordionWebComponent } from '../../../ui/gzAccordion';
import { AccordionAlign } from '../../../ui/gzAccordion/types';
import { emphasizeString } from '../../../utils/htmlElement.utils';
import { FINDING_DETAILS_BY_TYPE, FindingGroup, RelatedFinding } from '../../findingDetails/findingDetails/types';
import { EASMIconSet } from '../../types/iconSet';
import { html, render, TemplateResult } from 'lit';
import { EasmSideMenuEvents } from '../../easmSideMenu/types';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { GzTextWebComponent } from '../../../ui/gzText';
import { GzButtonWebComponent } from '../../../ui/gzButton';

export default class EasmRelatedFindingsView extends BaseWebComponentView {
  public dom: {
    $backButton: GzButtonWebComponent;
    $backButtonText: HTMLSpanElement;
    $panel: HTMLDivElement;
    $panelTitle: GzTitleWebComponent;
    $inputSearch: GzInputSearchWebComponent;
    $findingGroupsContainer: HTMLDivElement;
  };
  private firstRender = true;

  public getTranslations(): void {
    this.translations = {
      backButtonText: translateFn('Back'),
      panelTitles: {
        [FindingClassType.ASSET]: translateFn('Related assets'),
        [FindingClassType.ARTIFACT]: translateFn('Related artifacts'),
      },
      inputSearchPlaceholders: {
        [FindingClassType.ASSET]: translateFn('Enter at least {nrChars} characters for an asset.', {nrChars: MIN_NR_CHARS_SEARCH}),
        [FindingClassType.ARTIFACT]: translateFn('Enter at least {nrChars} characters for an artifact.', {nrChars: MIN_NR_CHARS_SEARCH}),
      },
      noResultsTexts: {
        [FindingClassType.ASSET]: translateFn('No assets found'),
        [FindingClassType.ARTIFACT]: translateFn('No artifacts found'),
      },
    };
  }

  public setTranslatedStrings(): void {
    this.dom.$backButtonText.innerText = this.translations.backButtonText;
  }

  public getDomElements(): void {
    this.dom.$backButton = this.shadow.querySelector('#back-btn');
    this.dom.$backButtonText = this.shadow.querySelector('#back-btn-text');
    this.dom.$panel = this.shadow.querySelector('#easm-related-findings-panel');
    this.dom.$panelTitle = this.shadow.querySelector('#panel-title');
    this.dom.$inputSearch = this.shadow.querySelector('#input-search');
    this.dom.$findingGroupsContainer = this.shadow.querySelector('#finding-groups-container');

    this.getTranslations();
    this.setTranslatedStrings();
  }

  public async buildSuccessView(data: EasmRelatedFindingsViewData): Promise<void> {
    this.dom.$panelTitle.innerText = this.translations.panelTitles[data.findingClassType];
    this.dom.$inputSearch.placeholder = this.translations.inputSearchPlaceholders[data.findingClassType];

    this.dom.$findingGroupsContainer.innerHTML = '';

    if (0 === data.findingGroups.length) {
      this.dom.$findingGroupsContainer.appendChild(this.buildNoResultsMessage(data.findingClassType));

      return;
    }

    for (const findingGroup of data.findingGroups) {
      this.dom.$findingGroupsContainer.appendChild(this.buildFindingGroupAccordion(findingGroup, data.searchText));
    }

    if (this.firstRender) {
      if (null !== data.findingTypeGroupToFocusOn) {
        await this.focusOnFindingGroupAccordion(data.findingTypeGroupToFocusOn);
      }

      this.firstRender = false;
    }
  }

  public buildErrorView(): void {
    this.resetTemplateState(this.dom.$panel);

    this.setTemplateState(this.dom.$panel, TemplateState.ERROR);
  }

  private buildNoResultsMessage(findingClassType: FindingClassType): GzTextWebComponent {
    const message = new GzTextWebComponent();

    message.id = 'no-results-message';
    message.fontWeight = FontWeights.NORMAL;
    message.fontSize = FontSizes.PARAGRAPH_12;

    message.innerText = this.translations.noResultsTexts[findingClassType];

    return message;
  }

  private buildFindingGroupAccordion(findingGroup: FindingGroup, searchText: string): GzAccordionWebComponent {
    const accordion = new GzAccordionWebComponent(),
      [title, hasSubtitle] = this.buildFindingGroupTitle(findingGroup);

    accordion.className = 'finding-group';
    accordion.setAttribute('data-type', String(findingGroup.type));

    accordion.align = AccordionAlign.LEFT;
    accordion.isOpen = true;
    accordion.removeTitlePadding = true;

    accordion.innerHTML = `
      <div slot="dynamic-title-content" class="finding-group-title-container"></div>

      <div class="container${hasSubtitle ? ' has-subtitle-above' : ''}"></div>
    `;

    render(title, accordion.querySelector('.finding-group-title-container') as HTMLDivElement);
    render(
      findingGroup.items.map(
        (finding: RelatedFinding): TemplateResult => this.buildFindingDetails(finding, searchText),
      ),
      accordion.querySelector('.container') as HTMLDivElement,
    );

    return accordion;
  }

  private buildFindingGroupTitle(findingGroup: FindingGroup): [TemplateResult, boolean] {
    const icon = EASMIconSet.createIconSet(findingTypeToIconMap[findingGroup.type] || 'ds-icon-unknown'),
      title = this.stringsTranslator.translateEASMFindingTypePluralized(findingGroup.type),
      findingDetails = FINDING_DETAILS_BY_TYPE[findingGroup.type],
      subtitle = findingDetails.getSubtitle(findingGroup);

    return [
      html`
        ${icon}

        <div class="finding-group-title-text">
          <div class="finding-group-title-count">
            <gz-tooltip class="finding-group-title" tooltiptext="${title}" isellipsis="true">${title}</gz-tooltip>

            <gz-text
              class="finding-group-count"
              fontWeight="${FontWeights.MEDIUM}"
              fontSize="${FontSizes.PARAGRAPH_10}"
            >
              (${findingGroup.count})
            </gz-text>
          </div>

          <div class="finding-group-subtitle">${subtitle}</div>
        </div>
      `,
      Boolean(subtitle),
    ];
  }

  private buildFindingDetails(finding: RelatedFinding, searchText: string): TemplateResult {
    const findingName = emphasizeString(searchText, finding.name);

    return html`
      <div class="row">
        <gz-tooltip class="column-1" isEllipsis tooltipText="${findingName}">
          <gz-link
            class="finding-name"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            @click="${(): void => this.handleFindingNameClick(finding)}"
          >
            ${unsafeHTML(findingName)}
          </gz-link>
        </gz-tooltip>

        <div class="column-2">${FINDING_DETAILS_BY_TYPE[finding.type].getRelatedInfo(finding)}</div>
      </div>
    `;
  }

  private handleFindingNameClick(finding: RelatedFinding): void {
    this.dom.$panel.dispatchEvent(
      new CustomEvent(EasmSideMenuEvents.OPEN_FINDING_DETAILS, {
        detail: {findingId: finding.id},
        bubbles: true,
        composed: true,
      }),
    );
  }

  private async focusOnFindingGroupAccordion(findingType: FindingType): Promise<void> {
    const accordion = this.dom.$findingGroupsContainer.querySelector(
      `.finding-group[data-type="${findingType}"]`,
    ) as GzAccordionWebComponent;

    if (!accordion) {
      return;
    }

    await accordion.updateComplete;

    const accordionHeader = accordion.shadowRoot?.querySelector('.accordion-header') as HTMLDivElement;

    accordionHeader.setAttribute('tabindex', '-1');
    accordionHeader.focus({preventScroll: false});
  }
}
